/**
 * Push Notification Service
 *
 * Handles web push notification subscriptions using the Push API and Service Workers.
 * This service manages subscription lifecycle, permission checks, and communication
 * with the backend push subscription API.
 */

import * as Sentry from '@sentry/react';
import axios from 'axios';

const NON_ACTIONABLE_PUSH_ERROR_NAMES = new Set(['AbortError', 'InvalidStateError', 'NotAllowedError']);

const isNonActionablePushSubscriptionError = (error: unknown) => {
  if (error instanceof DOMException && NON_ACTIONABLE_PUSH_ERROR_NAMES.has(error.name)) return true;

  return (
    error instanceof Error &&
    [...NON_ACTIONABLE_PUSH_ERROR_NAMES].some((errorName) => error.message.startsWith(`${errorName}:`))
  );
};

/**
 * Checks if push notifications are supported in the current browser.
 *
 * @returns True if both Service Workers and Push Manager are available
 */
export async function isPushSupported(): Promise<boolean> {
  return (
    typeof navigator.serviceWorker !== 'undefined' &&
    typeof window.PushManager !== 'undefined' &&
    typeof window.Notification !== 'undefined'
  );
}

/**
 * Gets the current notification permission state.
 *
 * @returns The notification permission state: 'granted', 'denied', or 'default'
 */
export async function getPermissionState(): Promise<NotificationPermission> {
  if (!(await isPushSupported())) return 'denied';

  return Notification.permission;
}

/**
 * Subscribes the user to push notifications.
 *
 * This function:
 * 1. Waits for the service worker to be ready
 * 2. Fetches the VAPID public key from the server
 * 3. Creates a push subscription with the browser
 * 4. Sends the subscription details to the backend for storage
 *
 * @returns True if subscription was successful, false otherwise
 */
export async function subscribeToPush(): Promise<boolean> {
  try {
    const registration = await getActiveRegistration();

    // Fetch the VAPID public key from the server
    const response = await fetch('/push_subscriptions/vapid_public_key');

    // A logged-out session redirects this request to the HTML login page, which
    // would make the JSON parse below throw. Bail out quietly in that case.
    if (!isJsonResponse(response)) return false;

    const { vapid_public_key } = await response.json();

    // Subscribe to push notifications through the browser
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid_public_key) as BufferSource,
    });

    // Send the subscription details to the backend
    await axios.post('/push_subscriptions', {
      push_subscription: {
        endpoint: subscription.endpoint,
        p256dh_key: arrayBufferToBase64(subscription.getKey('p256dh')),
        auth_key: arrayBufferToBase64(subscription.getKey('auth')),
      },
    });

    return true;
  } catch (error) {
    if (!isNonActionablePushSubscriptionError(error)) {
      Sentry.captureException(error, { tags: { flow: 'push_notifications', step: 'subscribe' } });
    }

    return false;
  }
}

/**
 * Checks whether a fetch response carries a JSON body.
 *
 * @param response - The fetch response to inspect
 * @returns True if the response is OK and its content type is JSON
 */
function isJsonResponse(response: Response): boolean {
  return response.ok && (response.headers.get('content-type')?.includes('application/json') ?? false);
}

/**
 * Waits for the service worker registration to have an active worker.
 *
 * Safari sometimes resolves `navigator.serviceWorker.ready` before the worker
 * has finished activating, leaving `registration.active` null. Calling
 * `pushManager.subscribe()` in that state throws an InvalidStateError
 * ("Subscribing for push requires an active service worker"), so we wait for
 * the installing/waiting worker to reach the 'activated' state first.
 *
 * @returns A registration whose service worker is active when possible
 */
async function getActiveRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.ready;
  if (registration.active) return registration;

  const worker = registration.installing ?? registration.waiting;
  if (!worker) return registration;

  await new Promise<void>((resolve) => {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') resolve();
    });
  });

  return registration;
}

/**
 * Unsubscribes the user from push notifications.
 *
 * This function:
 * 1. Gets the current subscription from the browser
 * 2. Unsubscribes from the browser's push manager
 * 3. Notifies the backend to remove the subscription
 *
 * @returns True if unsubscription was successful, false otherwise
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Unsubscribe from the browser
      await subscription.unsubscribe();

      // Notify the backend to delete the subscription
      await axios.delete(`/push_subscriptions/${encodeURIComponent(subscription.endpoint)}`);
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Gets the current push subscription, if one exists.
 *
 * @returns The current PushSubscription object, or null if not subscribed
 */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  try {
    if (!(await isPushSupported())) return null;

    const registration = await navigator.serviceWorker.ready;

    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Converts a URL-safe base64 string to a Uint8Array.
 *
 * The VAPID public key comes in URL-safe base64 format, which needs to be
 * converted to a Uint8Array for use with the Push API.
 *
 * @param base64String - The URL-safe base64 encoded string
 * @returns A Uint8Array representation of the decoded data
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  // Add padding if needed
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  // Convert URL-safe base64 to standard base64
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  // Decode base64 string
  const rawData = window.atob(base64);

  // Convert to Uint8Array
  return Uint8Array.from([...rawData].map((character) => character.charCodeAt(0)));
}

/**
 * Converts an ArrayBuffer to a base64 string.
 *
 * Used to encode the p256dh and auth keys from the push subscription
 * for transmission to the backend.
 *
 * @param buffer - The ArrayBuffer to encode
 * @returns A base64 encoded string, or empty string if buffer is null
 */
function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';

  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}
